import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from '@/services/api'
import { casesApi } from '@/services/api/cases'
import {
  type FindDuplicatesRequest,
  type GroupDismissRequest,
  type GroupMergeRequest,
  type GroupPage,
  type GroupRejectMemberRequest,
  groupReviewApi,
} from '@/services/api/entity-resolution-groups'
import { type Schema, schemasApi } from '@/services/api/schemas'

export const groupKeys = {
  all: (caseId: string) => ['case', caseId, 'entity-resolution', 'groups'] as const,
}

/** Prefer the API error's `detail` body (e.g. the 409 "group out of date"
 * message) over the generic "HTTP 409: Conflict" status line. */
function errorDetail(err: Error): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string } | undefined)?.detail
    if (detail) return detail
  }
  return err.message
}

/** Cluster cards are heavy (avatar stacks, expandable grids) — keep pages small. */
export const GROUPS_PAGE_SIZE = 25

export function useEntityGroups(caseId: string, options?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...groupKeys.all(caseId), options?.limit ?? null, options?.offset ?? null],
    queryFn: () => groupReviewApi.list(caseId, options),
    enabled: Boolean(caseId),
    staleTime: 60 * 1000,
    // Keep the previous page visible (dimmed) while the next one loads.
    placeholderData: keepPreviousData,
  })
}

export function useGroupPendingCount(caseId: string) {
  return useQuery({
    queryKey: [...groupKeys.all(caseId), 'pending-count'],
    queryFn: () => groupReviewApi.pendingCount(caseId),
    enabled: Boolean(caseId),
    staleTime: 30 * 1000,
  })
}

/** Drop merged members from every cached group page; prune emptied groups. */
function removeMembersFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
  ids: Iterable<string>
) {
  const drop = new Set(ids)
  queryClient.setQueriesData<GroupPage>({ queryKey: groupKeys.all(caseId) }, prev => {
    // `groupKeys.all` is a prefix match, so this also hits the pending-count
    // query (`{ candidates, groups: number }`) whose `groups` is a number, not
    // an array. Only transform the list pages; pending-count is refreshed by the
    // onSettled invalidation. Without this guard `prev.groups.map` throws
    // "prev.groups.map is not a function".
    if (!prev || !Array.isArray(prev.groups)) return prev
    const groups = prev.groups
      .map(g => ({ ...g, members: g.members.filter(m => !drop.has(m.id)) }))
      .map(g => ({ ...g, memberCount: g.members.length }))
      .filter(g => g.members.length > 1)
    // `total` is the case-wide group count (pagination), not the page length —
    // subtract only the groups this update pruned from the page.
    const pruned = prev.groups.length - groups.length
    return { groups, total: Math.max(0, prev.total - pruned) }
  })
}

export function useMergeGroup(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: GroupMergeRequest) => groupReviewApi.merge(caseId, body),
    onSuccess: (_data, body) => {
      removeMembersFromCache(queryClient, caseId, body.member_ids)
      toast.success('Merged into the chosen entity')
    },
    onError: (err: Error) => toast.error(errorDetail(err) || 'Merge failed'),
    onSettled: () => {
      // Prefix invalidation covers the group list, the pending-count badge AND
      // the audit log — so the audit list refetches when the analyst opens it
      // after a merge instead of showing a stale page until a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'entity-resolution'] })
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'graph'] })
    },
  })
}

/** Drop an entire group from every cached page — "none of this is a duplicate". */
function removeGroupFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
  groupId: string
) {
  queryClient.setQueriesData<GroupPage>({ queryKey: groupKeys.all(caseId) }, prev => {
    // Same prefix-match guard as removeMembersFromCache (pending-count query).
    if (!prev || !Array.isArray(prev.groups)) return prev
    const groups = prev.groups.filter(g => g.groupId !== groupId)
    const pruned = prev.groups.length - groups.length
    return { groups, total: Math.max(0, prev.total - pruned) }
  })
}

export function useDismissGroup(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: GroupDismissRequest) => groupReviewApi.dismissGroup(caseId, body),
    onSuccess: (_data, body) => {
      removeGroupFromCache(queryClient, caseId, body.group_id)
      toast.success('Cluster dismissed — entities left untouched')
    },
    onError: (err: Error) => toast.error(err.message || 'Could not dismiss cluster'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'entity-resolution'] })
    },
  })
}

export function useRejectGroupMember(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: GroupRejectMemberRequest) => groupReviewApi.rejectMember(caseId, body),
    onSuccess: (_data, body) => {
      removeMembersFromCache(queryClient, caseId, [body.member_id])
    },
    onError: (err: Error) => toast.error(err.message || 'Could not remove member'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: groupKeys.all(caseId) }),
  })
}

const ME_POLL_MS = 1500
const ME_MAX_POLLS = 600
// Refresh the visible queue every ~6s while the bulk merge runs, so the board
// visibly empties without hammering the list endpoint on every poll.
const ME_REFRESH_EVERY_POLLS = 4

/**
 * Bulk-merge fully-exact clusters (all candidate names have 100% similarity).
 * Runs as a polled background job;
 * mixed clusters are left untouched for manual review.
 */
export function useMergeExactDuplicates(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      let current = await groupReviewApi.mergeExact(caseId)
      for (let i = 0; current.status === 'running' && i < ME_MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, ME_POLL_MS))
        current = await groupReviewApi.getMergeExactStatus(caseId, current.job_id)
        if (i % ME_REFRESH_EVERY_POLLS === ME_REFRESH_EVERY_POLLS - 1) {
          queryClient.invalidateQueries({ queryKey: groupKeys.all(caseId) })
        }
      }
      if (current.status === 'running') throw new Error('Merge timed out')
      if (current.status === 'failed') throw new Error(current.error || 'Merge failed')
      return current
    },
    onSuccess: job => {
      const merged = job.result?.merged_groups ?? 0
      const members = job.result?.merged_members ?? 0
      const failed = job.result?.failed_groups ?? 0
      if (merged > 0) {
        toast.success(`Merged ${merged} exact-duplicate cluster(s) (${members} entities)`)
      } else {
        toast.info('No fully exact clusters to merge')
      }
      if (failed > 0) {
        toast.warning(`${failed} cluster(s) could not be merged — see the audit log`)
      }
    },
    onError: (err: Error) => toast.error(err.message || 'Merge failed'),
    onSettled: () => {
      // Prefix invalidation covers the group list, pending-count AND audit log.
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'entity-resolution'] })
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'graph'] })
    },
  })
}

const FD_POLL_MS = 1500
const FD_MAX_POLLS = 120

export function useFindDuplicates(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    // A property scan runs as a polled background job (like recalculate): POST
    // kicks it off, then we poll the status endpoint until it settles.
    mutationFn: async (body: FindDuplicatesRequest) => {
      let current = await groupReviewApi.findDuplicates(caseId, body)
      for (let i = 0; current.status === 'running' && i < FD_MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, FD_POLL_MS))
        current = await groupReviewApi.getFindDuplicatesStatus(caseId, current.job_id)
      }
      if (current.status === 'running') throw new Error('Search timed out')
      if (current.status === 'failed') throw new Error(current.error || 'Search failed')
      return current
    },
    onSuccess: job => {
      const created = job.result?.groups_created ?? 0
      // A re-run replaces the prior suggestions for this property rather than
      // stacking them — surface how many it dropped so the swap is visible.
      const cleared = job.result?.cleared ?? 0
      if (created > 0) {
        toast.success(
          `Found ${created} possible duplicate group(s)${cleared > 0 ? ` (replaced ${cleared})` : ''}`
        )
      } else if (cleared > 0) {
        toast.info(`Cleared ${cleared} previous suggestion(s) — none above the chosen tolerance`)
      } else {
        toast.info('No possible duplicates found above the chosen tolerance')
      }
      queryClient.invalidateQueries({ queryKey: groupKeys.all(caseId) })
    },
    onError: (err: Error) => toast.error(err.message || 'Search failed'),
  })
}

/** One entity type from the case schema, flattened for the find-duplicates form. */
export interface EntityTypeOption {
  label: string
  properties: string[]
}

/** Entity types (+ their property names) from the schema that applies to this
 * case — its override if set, otherwise the active schema. Drives the
 * find-duplicates dropdowns. */
export function useCaseEntityTypes(caseId: string, enabled: boolean) {
  return useQuery<EntityTypeOption[]>({
    queryKey: ['case', caseId, 'er-find-schema'],
    enabled: enabled && Boolean(caseId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      let schemaId: string | null = null
      try {
        schemaId = (await casesApi.getCaseSettings(caseId)).schema_id
      } catch {
        schemaId = null
      }
      let schema: Schema | null = null
      try {
        schema = schemaId ? await schemasApi.get(schemaId) : await schemasApi.getActive()
      } catch {
        schema = null
      }
      return (schema?.entity_types ?? []).map(et => ({
        label: et.label,
        properties: et.properties.map(p => p.name),
      }))
    },
  })
}
